import { useEffect, useState } from "react";

/**
 * Looks the person can pick from on the Appearance page. Each is a `data-theme` value on <html>
 * (see src/themes.css); the choice is remembered per browser, not per account, so a shared office
 * computer and a phone can differ.
 */
export const THEMES = [
  {
    id: "ledger",
    name: "Ledger",
    blurb: "Warm paper, numbered lines and one red mark. The original.",
    swatches: ["#f4f1ea", "#161513", "#e0442a"],
  },
  {
    id: "console",
    name: "Console",
    blurb: "A sidebar and cards on a white desk. Calm, organised software.",
    swatches: ["#f3f5f7", "#1c2126", "#0f766e"],
  },
  {
    id: "noticeboard",
    name: "Noticeboard",
    blurb: "White cards pinned to a slate board, like the window of an agency.",
    swatches: ["#50606f", "#ffffff", "#d7263d"],
  },
  {
    id: "gazette",
    name: "Gazette",
    blurb: "Newsprint and a serif masthead, the appointments pages of the trade press.",
    swatches: ["#f1f1ed", "#121212", "#1a7f4b"],
  },
  {
    id: "night",
    name: "Night desk",
    blurb: "Dark teal with lilac highlights, for the evening shift.",
    swatches: ["#0e1f22", "#e6efee", "#b8a9f5"],
  },
  {
    id: "studio",
    name: "Studio",
    blurb: "Graph paper with ultramarine ink; section labels sit in the margin.",
    swatches: ["#ffffff", "#0b1326", "#2b3fd6"],
  },
  {
    id: "kudu",
    name: "Kudu",
    blurb: "Ochre, bone and umber from the animal itself, with its stripes across the top.",
    swatches: ["#f6f1e7", "#3a2e25", "#c9962b"],
  },
] as const;

export type ThemeId = (typeof THEMES)[number]["id"];
export const DEFAULT_THEME: ThemeId = "ledger";
const KEY = "kudu.theme";

/* Fonts beyond the Ledger's are loaded only when their theme is chosen, so the first visit stays light. */
const FONTS: Record<ThemeId, () => Promise<unknown>> = {
  ledger: () => Promise.resolve(),
  console: () => import("@fontsource-variable/albert-sans"),
  noticeboard: () => Promise.resolve(),
  gazette: () => Promise.all([import("@fontsource-variable/newsreader"), import("@fontsource-variable/newsreader/wght-italic.css")]),
  night: () => Promise.resolve(),
  studio: () => Promise.all([import("@fontsource-variable/red-hat-display"), import("@fontsource-variable/red-hat-text")]),
  kudu: () => import("@fontsource-variable/rubik"),
};

export const isTheme = (v: unknown): v is ThemeId => THEMES.some((t) => t.id === v);

export function storedTheme(): ThemeId {
  try {
    const v = localStorage.getItem(KEY);
    return isTheme(v) ? v : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

const listeners = new Set<(t: ThemeId) => void>();

/** Sets the theme on <html> (and loads its fonts); `remember` writes it for next time. */
export function applyTheme(id: ThemeId, remember = true) {
  document.documentElement.dataset.theme = id;
  void FONTS[id]().catch(() => {});
  if (remember) {
    try {
      localStorage.setItem(KEY, id);
    } catch {
      /* private mode: the theme lasts for this visit only */
    }
  }
  for (const l of listeners) l(id);
}

/** The current theme and a setter; shared by every component that shows or changes it. */
export function useTheme(): [ThemeId, (id: ThemeId) => void] {
  const [theme, setTheme] = useState<ThemeId>(() => {
    const v = document.documentElement.dataset.theme;
    return isTheme(v) ? v : storedTheme();
  });
  useEffect(() => {
    listeners.add(setTheme);
    return () => void listeners.delete(setTheme);
  }, []);
  return [theme, applyTheme];
}
